import {inject, Injectable} from '@angular/core';
import {FileParams} from "../../../../../core/types/file.types";
import {ButtonAction} from "../../../../../core/types/button-action.types";
import {GetFileParams} from "../../../../../core/components/modals/files-modal/types/files-current-modal.types";
import {FilesService} from "../../../../../core/services/files.service";
import {PreviewFilesStateService} from "../../../../../core/services/preview-files-state.service";
import {FileCardActionStateService} from "./file-card-action-state.service";
import {FileDownloadService} from "../../../../../core/services/file-download.service";
import {UtilityService} from "../../../../../core/services/utility.service";
import {defaultDeleteConfirmationParams} from "../../../../../core/models/default-delete-confirmation-params";
import {DeleteConfirmationParams} from "../../../../../core/types/confirmation-modal.types";
import {ConfirmService} from "../../../../../core/services/confirm.service";
import {FileCard} from "../../../../../core/types/file-card.types";
import {ValidationFilesService} from "../../../../../core/services/validation-files.service";
import {ModalsService} from "../../../../../core/services/modals.service";

@Injectable({
  providedIn: 'root'
})
export class FileCardService {
  private filesService: FilesService = inject(FilesService);
  private previewFilesStateService: PreviewFilesStateService = inject(PreviewFilesStateService);
  private fileDownloadService: FileDownloadService = inject(FileDownloadService);
  private fileCardActionStateService: FileCardActionStateService = inject(FileCardActionStateService);
  private utilityService: UtilityService = inject(UtilityService);
  private confirmService: ConfirmService = inject(ConfirmService);
  private validationFilesService: ValidationFilesService = inject(ValidationFilesService);
  private modalsService: ModalsService = inject(ModalsService);

  public async getPreviewFiles(id: number): Promise<void> {
    const url = await this.filesService.downloadFileNoEvents(id)
      .catch(() => '');
    url && this.previewFilesStateService.updatePreviewFilesState(id, url);
  }

  public isImage(fileCard: FileCard): boolean {
    if (!fileCard.shouldCheckImageType) { return false; }

    return this.validationFilesService.isImage(fileCard.fileParams.filename);
  }

  public getInfoTooltip(fileParams: FileParams): string {
    const { creationDate, uploadedBy } = fileParams;
    const date = this.utilityService.formatDateTimeToRussianStyle(creationDate);
    if (!date && !uploadedBy) { return 'Информация'; }

    const tooltip: Array<string> = [];
    uploadedBy && tooltip.push(uploadedBy);
    date && tooltip.push(date);

    return tooltip.join('\n');
  }

  public async downloadFile(fileParams: FileParams, buttonAction: ButtonAction): Promise<void> {
    const defaultFileName = fileParams.filename || 'downloaded-file';
    const getFileParams: GetFileParams = {
      fileId: fileParams.id,
      fileName: defaultFileName,
      fileSize: fileParams.size,
      type: buttonAction
    };

    this.fileCardActionStateService.setActionState(true, fileParams.id);
    await this.fileDownloadService.getFile(getFileParams);
    this.fileCardActionStateService.resetActionState();
  }

  public async handleOpenAction(fileCard: FileCard, isNeedArrow: boolean): Promise<void> {
    if (!this.isImage(fileCard)) {
      await this.downloadFile(fileCard.fileParams, 'open');
      return;
    }

    await this.modalsService.openPreviewFileModal({
      type: ['image', ''],
      url: this.previewFilesStateService.getFileUrl(fileCard.fileParams.id),
      fileId: fileCard.fileParams.id,
      fileType: fileCard.fileType,
      isNeedArrow: isNeedArrow
    });
  }

  public async getDeleteConfirmation(): Promise<boolean> {
    const deleteConfirmationParams: DeleteConfirmationParams = {
      ...defaultDeleteConfirmationParams,
      titleText: 'Удалить файл',
      description: 'Вы уверены, что хотите удалить файл?'
    };

    return this.confirmService.getDeleteConfirmation(deleteConfirmationParams);
  }
}
